import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Trash2, Shield, Loader2, Building2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Standalone Delete-My-Data card for the Profile page.
 * Kept lightweight (no Plaid SDK) so it can coexist with BankAccountManager
 * on Dashboard without dual-initialising react-plaid-link.
 */
export default function DeleteMyDataButton() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/users/me/data`, {
        withCredentials: true,
      });
      const items = res.data?.summary?.plaid_items_revoked ?? 0;
      toast.success(`All data deleted. ${items} bank connection(s) revoked.`);
      setOpen(false);
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete data');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="border-stone-200" data-testid="delete-my-data-card">
        <CardContent className="p-5 space-y-4">
          {/* Manage bank connections shortcut */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-stone-900 text-sm">Bank connections</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  Manage linked accounts, reconnect broken items, and view balances.
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="profile-manage-banks-link"
            >
              Manage
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="h-px bg-stone-100"></div>

          {/* Danger zone */}
          <div className="flex items-center justify-between gap-4 bg-rose-50/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-rose-900 text-sm">Delete all my data</div>
                <div className="text-xs text-rose-700/80 mt-0.5 max-w-md">
                  Revokes every Plaid bank connection, wipes transactions and profile, logs you out. Cannot be undone.
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(true)}
              className="border-rose-300 text-rose-700 hover:bg-rose-100"
              data-testid="delete-my-data-button"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-my-data-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-rose-700">
              <Trash2 className="w-5 h-5 mr-2" />
              Permanently delete your data
            </DialogTitle>
            <DialogDescription>
              This revokes every Plaid bank connection, erases your transactions and profile, and logs you out. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900">
            You&apos;ll be redirected to the homepage right after.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-my-data-button"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                'Yes, delete everything'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
